import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Form } from 'antd';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { SelectPidType } from '@/entities/personalIdentifiers';
import { Input } from '@/shared/components';
import { InputPhoneNumber } from '@/shared/components/forms/fields/InputPhoneNumber';
import { SelectCountry } from '@/shared/components/forms/fields/SelectCountry';
import { SelectGender } from '@/shared/components/forms/fields/SelectGender';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { CollectionName } from '@/shared/lib/sj-orm/constants';
import { generateDtoId } from '@/shared/lib/sj-orm/helpers';
// import { EMAIL_REGEX } from '@/shared/lib/hooks/use-form-render/helpers';
import { BeneficiaryDto } from '@/shared/lib/sj-orm/models/beneficiary.dto';
import { PersonalIdentifierType } from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { DatePicker } from '@/shared/uikit/DatePicker';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import {
  InterwillFieldsLineWrapper,
  InterwillFormContainer,
  InterwillStyledForm,
  InterwillSubmitButtonWrapper,
} from '../CreateEntityModal/styled';

import { UnregBeneficiaryData } from './types';

type CreateBeneficiaryModalV2Props = {
  title?: string;
  btnText?: string;
  onSubmit: (data: BeneficiaryDto) => void;
};

export const CreateBeneficiaryModalV2: FC<CreateBeneficiaryModalV2Props> = ({
  onSubmit,
  title,
  btnText,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'createBeneficiaryV2' });
  const isDesktop = useIsDesktop();
  const modals = useModalsStore();

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState,
    setValue,
    watch,
  } = useForm<UnregBeneficiaryData>();

  const handleSubmit = async (values: UnregBeneficiaryData) => {
    const data: UnregBeneficiaryData = {
      ...values,
      id: generateDtoId(CollectionName.BENEFICIARIES),
    };

    modals.closeModal();
    onSubmit(data as unknown as BeneficiaryDto);
  };

  const [phoneCode, phoneNumber] = watch(['phoneCode', 'phoneNumber']);

  return (
    <ModalContainerV1 title={title ?? t('title')}>
      <InterwillStyledForm onFinish={formHandleSubmit(handleSubmit)}>
        <InterwillFormContainer className="uikitScrollbar">
          <div>{t.rich('groupCommon')}</div>
          <Controller
            name={'firstName'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input
                    placeholder={t(`fields.${field.name}.placeholder`)}
                    type={'text'}
                    {...field}
                  />
                </Form.Item>
              </FieldContainerIw>
            )}
          />
          <InterwillFieldsLineWrapper>
            <Controller
              name={'secondName'}
              control={control}
              // rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              )}
            />
            <Controller
              name={'lastName'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              )}
            />
          </InterwillFieldsLineWrapper>
          <InterwillFieldsLineWrapper>
            <Controller
              name={'dateOfBirth'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FieldContainerIw required label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <DatePicker placeholder={t(`fields.${field.name}.placeholder`)} {...field} />
                  </Form.Item>
                </FieldContainerIw>
              )}
            />
            <Controller
              name={'gender'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FieldContainerIw required label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <SelectGender placeholder={t(`fields.${field.name}.placeholder`)} {...field} />
                  </Form.Item>
                </FieldContainerIw>
              )}
            />
          </InterwillFieldsLineWrapper>

          <GroupLabel>{t('groupCitizenship')}</GroupLabel>
          <Controller
            name={'countryCitizenship'}
            control={control}
            // rules={{ required: true }}
            render={({ field, fieldState }) => (
              <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <SelectCountry placeholder={t(`fields.${field.name}.placeholder`)} {...field} />
                </Form.Item>
              </FieldContainerIw>
            )}
          />
          <InterwillFieldsLineWrapper>
            <Controller
              name={'documentType'}
              control={control}
              // rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <SelectPidType
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      types={[PersonalIdentifierType.PASSPORT, PersonalIdentifierType.NATIONAL_ID]}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              )}
            />
            <Controller
              name={'documentNumber'}
              control={control}
              // rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      maxLength={128}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              )}
            />
          </InterwillFieldsLineWrapper>
          <Controller
            name={'issueDate'}
            control={control}
            // rules={{ required: true }}
            render={({ field, fieldState }) => (
              <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <DatePicker placeholder={t(`fields.${field.name}.placeholder`)} {...field} />
                </Form.Item>
              </FieldContainerIw>
            )}
          />

          <GroupLabel>{t('groupResidence')}</GroupLabel>
          <Controller
            name={'countryResidence'}
            control={control}
            // rules={{ required: true }}
            render={({ field, fieldState }) => (
              <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <SelectCountry placeholder={t(`fields.${field.name}.placeholder`)} {...field} />
                </Form.Item>
              </FieldContainerIw>
            )}
          />
          <InterwillFieldsLineWrapper>
            <Controller
              name={'district'}
              control={control}
              // rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              )}
            />
            <Controller
              name={'city'}
              control={control}
              // rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              )}
            />
          </InterwillFieldsLineWrapper>
          <InterwillFieldsLineWrapper>
            <Controller
              name={'street'}
              control={control}
              // rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              )}
            />
            <Controller
              name={'apartment'}
              control={control}
              // rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              )}
            />
          </InterwillFieldsLineWrapper>

          <GroupLabel>{t('groupContact')}</GroupLabel>
          <InterwillFieldsLineWrapper>
            <FieldContainerIw label={t(`fields.phone.label`)}>
              <InputPhoneNumber
                value={{
                  phoneCode,
                  phoneNumber: String(phoneNumber),
                }}
                placeholder={t(`fields.phone.placeholder`)}
                setPhoneCode={(value) => setValue('phoneCode', value)}
                setPhoneNumber={(value) => {
                  if (!isNaN(Number(value))) {
                    setValue('phoneNumber', Number(value));
                  }
                  if (!value) {
                    setValue('phoneNumber', undefined);
                  }
                }}
                style={{ height: 40 }}
                // {...field}
              />
            </FieldContainerIw>
            {/* <Controller
              name={'phoneNumber'}
              control={control}
              // rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <InputPhoneNumber
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      setPhoneCode={value => setValue('phoneCode', value)}
                      setPhoneNumber={value => setValue('phoneNumber', value)}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              )}
            /> */}
            <Controller
              name={'email'}
              control={control}
              // rules={{
              //   pattern: {
              //     value: EMAIL_REGEX,
              //     message: t('fields.email.incorrectEmailError'),
              //   },
              //   // required: t('fields.email.error'),
              // }}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              )}
            />
          </InterwillFieldsLineWrapper>
        </InterwillFormContainer>

        <InterwillSubmitButtonWrapper>
          <ButtonIw
            htmlType="submit"
            block={!isDesktop}
            loading={formState.isSubmitting}
            disabled={formState.isSubmitting}
          >
            {btnText ?? t('btn')}
          </ButtonIw>
        </InterwillSubmitButtonWrapper>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const GroupLabel = styled.div`
  margin-top: 8px;
  font-weight: 700;
`;
